import { useQuery } from "@tanstack/react-query";
import type { components } from "@qorakul/shared/api";
import { api } from "./api.ts";
import { ApiError } from "./apiError.ts";

export type FamilyBalance = components["schemas"]["FamilyBalance"];
export type StudentBalance = components["schemas"]["StudentBalance"];

export const balanceKeys = {
  family: (familyId: string) => ["balance", "families", familyId] as const,
};

/** Each child's balance and the family total; positive is credit, negative is debt. Read-only (balance.read). */
export function useFamilyBalance(familyId: string) {
  return useQuery({
    queryKey: balanceKeys.family(familyId),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/balance/families/{familyId}", { params: { path: { familyId } } });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
  });
}

/** The API sends money as a JSON number (its schema also allows a numeric string); amounts have at most two decimals. */
export function toAmount(value: number | string): number {
  return typeof value === "number" ? value : Number(value);
}

const sumFormat = new Intl.NumberFormat("uz-Latn-UZ", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** "1 250 000" — the amount without sign or currency; the caller adds both through i18n. */
export function formatSum(value: number): string {
  return sumFormat.format(Math.abs(value));
}

/** "−249 999,5" for a debt, "1 250 000" for a credit: the amount with a minus sign when negative. */
export function formatSignedSum(value: number): string {
  return value < 0 ? `\u2212${formatSum(value)}` : formatSum(value);
}
